import React from "react";
import { SourceWipsCardProps, Source } from "../../types/traceability";
import { kgFmt, pcsFmt } from "../../shared/formatting";
import { formatDate } from "../../shared/utilityTsx";

export default function SourceWipsCard({
  lotNumber,
  source,
  printShippedItemsReport,
}: SourceWipsCardProps) {
  if (!source) {
    return null;
  }

  const isReceiving = !source.is_wip;

  return (
    <div className="card-custom bg-white p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Lot Number: {lotNumber}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Source WIP or Receiving source for this lot number
          </p>
        </div>
        <button
          onClick={() => printShippedItemsReport(lotNumber)}
          className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
        >
          View Shipped Items
        </button>
      </div>

      <div className="space-y-4">
        <div
          key={`source-${
            isReceiving
              ? `receiving-${source.receiving_invoice_id}`
              : `wip-${source.wip_id}`
          }`}
          className="border border-gray-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <div>
              {isReceiving ? (
                <>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Receiving Source
                    </h3>
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                      Supplier
                    </span>
                  </div>
                  {source.receiving_invoice_number && (
                    <p className="text-sm text-gray-600 mt-1">
                      Invoice: {source.receiving_invoice_number}
                    </p>
                  )}
                  {source.supplier_name && (
                    <p className="text-sm text-gray-600">
                      Supplier: {source.supplier_name}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <h3 className="text-lg font-semibold text-gray-900">
                    WIP {source.wip_lot_number || `#${source.wip_id}`}
                  </h3>
                  <p className="text-sm text-gray-600">
                    WIP ID: {source.wip_id}
                  </p>
                </>
              )}
            </div>
            <div className="text-right">
              {isReceiving && source.receiving_invoice_id ? (
                <button
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  onClick={() =>
                    window.open(
                      `/receiving/${source.receiving_invoice_id}`,
                      "_blank"
                    )
                  }
                >
                  View Receiving
                </button>
              ) : !isReceiving && source.wip_id ? (
                <button
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  onClick={() =>
                    window.open(`/wips/${source.wip_id}`, "_blank")
                  }
                >
                  View WIP
                </button>
              ) : null}
            </div>
          </div>

          {isReceiving ? (
            // Receiving source metrics
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {source.receiving_date && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Receiving Date
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {formatDate(source.receiving_date)}
                  </div>
                </div>
              )}
              {source.total_received_kg !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Total Received (kg)
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {kgFmt(source.total_received_kg)}
                  </div>
                </div>
              )}
              {source.total_received_pcs !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Total Received (pcs)
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {pcsFmt(source.total_received_pcs)}
                  </div>
                </div>
              )}
              {source.receiving_carton_count !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Carton Count
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {source.receiving_carton_count || 0}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // WIP source metrics
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {source.total_produced_kg !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Total Produced (kg)
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {kgFmt(source.total_produced_kg)}
                  </div>
                </div>
              )}
              {source.total_produced_pcs !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Total Produced (pcs)
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {pcsFmt(source.total_produced_pcs)}
                  </div>
                </div>
              )}
              {source.carton_count !== undefined && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-gray-600 mb-1">
                    Carton Count
                  </div>
                  <div className="text-xl font-semibold text-gray-900">
                    {source.carton_count || 0}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
